import { type City, type CityArea, useV1CityAreas } from "@hermes/api";
import { useLocale, useMessage } from "@hermes/intl";
import {
  cssFns,
  Select,
  type SelectImperativeRef,
  TextField,
  type TriggerChildrenProps,
  useFormField,
  usePrincipalColors,
  useSelectState,
  useTextFieldState,
} from "@hermes/ui";
import { type RefObject, useEffect, useMemo, useState } from "react";
import { useParams as useRouteParams } from "react-router-dom";

import { MobileArrowLeft } from "#internal/shared/ui/icons";

import { useCity } from ".";
import { useCityArea } from "./city-area";

export const useCityRouteInfo = () => {
  const params = useRouteParams();

  const routeCitySlug = params["city"];

  return {
    routeCitySlug,
  };
};

export const useResolveCityRouteInfo = ({ cities }: { cities: City[] }) => {
  const { routeCitySlug } = useCityRouteInfo();
  const routeCity = cities.find((c) => c.slug === routeCitySlug);

  return {
    routeCitySlug,
    routeCity,
  };
};

export const CitySelect = ({
  cityRef,
  children,
}: {
  cityRef?: RefObject<SelectImperativeRef>;
} & TriggerChildrenProps) => {
  const { city, changeCityBySlug, resolveCityById, cities } = useCity();
  const { cityArea, changeCityAreBySlug } = useCityArea();
  const [locale] = useLocale();

  const message = useMessage();
  const principalColors = usePrincipalColors();

  const [areasCache, setAreasCache] = useState(new Map<number, CityArea[]>());
  const [submenuCityId, setSubmenuCityId] = useState<number | undefined>(
    cityArea ? city.id : undefined,
  );

  const submenuCity = resolveCityById(submenuCityId);
  const { data: submenuAreasData, isFetching } = useV1CityAreas(
    { city: submenuCityId!, locale: locale.baseName },
    {
      enabled: !!submenuCityId,
    },
  );

  useEffect(() => {
    if (submenuCityId && submenuAreasData?.results) {
      setAreasCache((cache) => {
        cache.set(submenuCityId, submenuAreasData.results);
        return new Map(cache);
      });
    }
  }, [submenuAreasData?.results, submenuCityId]);

  const mobileFilterField = useFormField(useTextFieldState());
  const mobileSubmenuFilterField = useFormField(useTextFieldState());

  const field = useFormField(
    useSelectState<{ city: City; cityArea?: CityArea }>({
      value: null,
      parent: useMemo(
        () => ({
          city,
          cityArea,
        }),
        [city, cityArea],
      ),
    }),
  );

  const options = cities
    .filter((city) => {
      if (mobileFilterField.value) {
        return city.name.includes(mobileFilterField.value);
      }
      return true;
    })
    .map((city) => {
      const areas = areasCache.get(city.id);
      const options = [
        {
          label: message({
            id: "009beb03-6b61-4c21-a456-245d3a5919b1",
            default: "Все районы",
          }),
          value: { city, cityArea: undefined },
        },
        ...(areas?.map((area) => ({
          label: area.name,
          value: {
            city,
            cityArea: area,
          },
        })) ?? []),
      ];

      return {
        label: city.name,
        value: { city } as { city: City; cityArea?: CityArea },
        hasSubOptions: true,
        subOptions: options.filter((area) => {
          if (mobileSubmenuFilterField.value) {
            return area.label.includes(mobileSubmenuFilterField.value);
          }
          return true;
        }),
        areSubOptionsLoading: isFetching,
      };
    });

  return (
    <Select
      imperativeRef={cityRef}
      field={field}
      showParent
      onSelectionChange={({ value }) => {
        changeCityBySlug(value.city.slug, value.cityArea?.slug);
        changeCityAreBySlug(value.cityArea?.slug);
      }}
      mobile={{
        title: (
          <div css={[{ width: "100%" }, cssFns.padding(14)]}>
            <TextField
              field={mobileFilterField}
              placeholder={city.name}
              clearable
            />
          </div>
        ),
        submenuTitle: ({ close }) => (
          <div css={[{ width: "100%" }, cssFns.padding(14)]}>
            <TextField
              icon={
                <MobileArrowLeft
                  color={principalColors.ebblue}
                  width={24}
                  height={24}
                />
              }
              onIconPress={() => {
                close();
              }}
              field={mobileSubmenuFilterField}
              placeholder={
                submenuCity
                  ? message({
                      id: "e9b65e09-82ab-489a-afd4-29325d7be94f",
                      default: "Район в {city}",
                      values: {
                        city: submenuCity.name,
                      },
                    })
                  : undefined
              }
              clearable
            />
          </div>
        ),
      }}
      options={options}
      toKeyString={({ city, cityArea }) =>
        `${city.id}${cityArea?.id ? `-${cityArea.id}` : ""}`
      }
      onSubmenuOpen={(key) => {
        setSubmenuCityId(Number(key));
      }}
      onSubmenuClose={() => {
        setSubmenuCityId(undefined);
      }}
      children={children}
    />
  );
};
